import { Link } from "react-router-dom";
import {
  Printer, CalendarCheck, ClipboardList, Users, GraduationCap, Video, Dumbbell,
  Target, Award, TrendingUp, CheckCircle2, ArrowRight, BookOpen, ListChecks, Rocket,
} from "lucide-react";

const GOLD = "#c9a961";

const Step = ({ n, children }) => (
  <li className="flex gap-3">
    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-600 text-black font-bold text-xs flex items-center justify-center">{n}</span>
    <span className="text-zinc-200 text-sm leading-relaxed">{children}</span>
  </li>
);

const GoTo = ({ to, label }) => (
  <Link to={to} className="inline-flex items-center gap-1.5 mt-3 bg-amber-600/15 border border-amber-500/40 text-amber-200 hover:bg-amber-600/25 px-3 py-1.5 rounded-lg text-xs font-semibold">
    Ouvrir : {label} <ArrowRight className="w-3.5 h-3.5" />
  </Link>
);

const Card = ({ icon: Icon, id, title, subtitle, children }) => (
  <section id={id} className="border border-zinc-800 rounded-2xl p-5 bg-zinc-900/40 scroll-mt-24" data-testid={`guide-${id}`}>
    <div className="flex items-start gap-3 mb-3">
      <div className="w-11 h-11 rounded-xl bg-amber-600/20 flex items-center justify-center flex-shrink-0"><Icon className="w-6 h-6 text-amber-400" /></div>
      <div>
        <h2 className="text-lg font-black text-zinc-100">{title}</h2>
        {subtitle && <p className="text-xs text-zinc-500">{subtitle}</p>}
      </div>
    </div>
    {children}
  </section>
);

const NAV = [
  ["presences", "Prendre les présences"],
  ["suivi", "Suivi des élèves"],
  ["cours", "Monter un cours"],
  ["evolution", "Évolution & ceintures"],
  ["techniques", "Techniques & recommandations"],
  ["pedagogie", "Ta pédagogie de coach"],
  ["formation", "Ta progression & formations"],
  ["equipe", "Piloter l'évolution de l'équipe"],
  ["checklist", "Recommandations à voir ensemble"],
];

export default function GuideCoachModule() {
  return (
    <div className="p-6 max-w-5xl mx-auto text-zinc-100" data-testid="guide-coach">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-2"><GraduationCap style={{ color: GOLD }} /> Guide Coach — Kaizen System</h1>
          <p className="text-sm text-zinc-400 mt-1">Comment fonctionne la plateforme, module par module. À projeter et parcourir avec l'équipe.</p>
        </div>
        <button onClick={() => window.print()} data-testid="print-guide-btn" className="inline-flex items-center gap-2 border border-zinc-600 text-zinc-200 hover:bg-zinc-800 px-4 py-2 rounded-lg text-sm font-semibold">
          <Printer className="w-4 h-4" /> Imprimer / PDF
        </button>
      </div>

      {/* Ordre du jour */}
      <div className="border border-amber-700/40 rounded-2xl p-5 bg-amber-950/10 my-5">
        <h2 className="text-sm font-black text-amber-300 flex items-center gap-2 mb-3"><ListChecks className="w-4 h-4" /> Ordre du jour de la rencontre</h2>
        <div className="grid sm:grid-cols-3 gap-2">
          {NAV.map(([id, label], i) => (
            <a key={id} href={`#${id}`} className="text-sm text-zinc-300 hover:text-amber-300 flex items-center gap-2">
              <span className="text-amber-500 font-bold">{i + 1}.</span> {label}
            </a>
          ))}
        </div>
      </div>

      <div className="grid gap-4">
        <Card icon={CalendarCheck} id="presences" title="1. Prendre les présences" subtitle="Chaque cours, en 30 secondes">
          <ol className="space-y-2">
            <Step n="1">Ouvre <b>Présences</b> et choisis le cours et la date du jour.</Step>
            <Step n="2">Coche chaque élève présent — le système enregistre l'assiduité automatiquement dans son dossier.</Step>
            <Step n="3">Pour un <b>cours d'essai</b>, utilise plutôt <b>Check-in Cours d'essai</b> (le prospect scanne le QR ou tu le coches).</Step>
            <Step n="4">Les absences répétées ressortent ensuite dans le suivi des élèves (relances possibles).</Step>
          </ol>
          <div className="flex gap-2 flex-wrap"><GoTo to="/app/presences" label="Présences" /><GoTo to="/app/checkin-essai" label="Check-in Cours d'essai" /></div>
        </Card>

        <Card icon={Users} id="suivi" title="2. Faire le suivi des élèves" subtitle="Dossier, contrat, progression, relances">
          <ol className="space-y-2">
            <Step n="1"><b>Carnet Progression</b> : le dossier de chaque élève — ceinture actuelle, techniques maîtrisées, notes du coach.</Step>
            <Step n="2"><b>Contrats Élèves</b> : abonnement, statut, formalités (waiver), équipement du premier mois.</Step>
            <Step n="3"><b>Centre de suivis</b> : la vue d'ensemble pour repérer qui a besoin d'attention (absences, retards, à féliciter).</Step>
            <Step n="4">Ajoute une note ou un objectif à un élève directement dans son carnet après chaque cours important.</Step>
          </ol>
          <div className="flex gap-2 flex-wrap"><GoTo to="/app/carnet-progression" label="Carnet Progression" /><GoTo to="/app/eleves" label="Contrats Élèves" /><GoTo to="/app/centre-suivis" label="Centre de suivis" /></div>
        </Card>

        <Card icon={ClipboardList} id="cours" title="3. Monter un cours dans la plateforme" subtitle="Plan de cours + horaire + curriculum">
          <ol className="space-y-2">
            <Step n="1"><b>Plans de cours KMT</b> : construis le plan (échauffement, techniques, rounds, retour au calme) relié au curriculum.</Step>
            <Step n="2"><b>Horaire des cours</b> : place le cours dans la grille (jour, heure, discipline, coach) — visible des membres.</Step>
            <Step n="3"><b>Couverture Curriculum</b> : vérifie que ton plan couvre bien les techniques attendues pour la ceinture visée.</Step>
            <Step n="4">Besoin d'une séance guidée courte à donner en devoir ? Vois <b>Entraînements associés</b> (20-30 min, reliés aux techniques).</Step>
          </ol>
          <div className="flex gap-2 flex-wrap"><GoTo to="/app/plans-cours-kmt" label="Plans de cours KMT" /><GoTo to="/app/horaire" label="Horaire des cours" /><GoTo to="/app/couverture-curriculum" label="Couverture Curriculum" /></div>
        </Card>

        <Card icon={TrendingUp} id="evolution" title="4. Suivre l'évolution & les ceintures" subtitle="Évaluation → certificat, sans automatisme">
          <ol className="space-y-2">
            <Step n="1"><b>Carnet Progression</b> : coche les techniques maîtrisées au fil des cours — la barre de progression de l'élève avance.</Step>
            <Step n="2"><b>Évaluation Live</b> : le jour du test de ceinture, évalue technique par technique en direct.</Step>
            <Step n="3"><b>Certificats</b> : génère le certificat de passage une fois l'évaluation réussie.</Step>
            <Step n="4"><b>Important</b> : une séance ou un entraînement terminé ne change JAMAIS la ceinture tout seul — c'est toujours toi qui valides.</Step>
          </ol>
          <div className="flex gap-2 flex-wrap"><GoTo to="/app/evaluation" label="Évaluation Live" /><GoTo to="/app/certificats" label="Certificats" /><GoTo to="/app/grille-kaizen-v2" label="Grille Kaizen V2" /></div>
        </Card>

        <Card icon={Video} id="techniques" title="5. Voir les techniques à pratiquer & les recommandations" subtitle="Vidéos, bibliothèque, entraînements guidés">
          <ol className="space-y-2">
            <Step n="1"><b>Vidéos techniques</b> : la banque de capsules par discipline, par ceinture, dans l'ordre du cours.</Step>
            <Step n="2"><b>Bibliothèque Tech.</b> : recherche une technique précise et ses détails.</Step>
            <Step n="3"><b>Entraînements associés</b> : séances guidées 20-30 min reliées à chaque technique — parfait à recommander aux élèves entre les cours (pilote Ceinture Orange déjà en ligne).</Step>
            <Step n="4">Astuce démo : utilise <b>Aperçu membre (accès total)</b> pour voir exactement ce que l'élève voit dans son portail.</Step>
          </ol>
          <div className="flex gap-2 flex-wrap"><GoTo to="/app/videos" label="Vidéos techniques" /><GoTo to="/app/bibliotheque-techniques" label="Bibliothèque Tech." /><GoTo to="/app/entrainements-associes" label="Entraînements associés" /><GoTo to="/app/apercu-membre" label="Aperçu membre" /></div>
        </Card>

        <Card icon={Target} id="pedagogie" title="6. Le suivi de TA pédagogie de coach" subtitle="Espace Coach : objectifs, impressions, couverture">
          <ol className="space-y-2">
            <Step n="1"><b>Espace Coach</b> : ta fiche (discipline, ceinture, rôle) et ton objectif pédagogique du moment (ex. « faire passer 3 membres au niveau suivant »).</Step>
            <Step n="2"><b>Impressions</b> : le compteur montre l'activité/consultations de ton mini-espace — un indicateur d'engagement.</Step>
            <Step n="3"><b>Couverture Curriculum</b> : mesure si, sur le mois, tu as bien enseigné l'ensemble des techniques prévues (pas de trou pédagogique).</Step>
            <Step n="4">« Lien Espace » te donne un mini-espace privé à partager avec ton assistant/remplaçant.</Step>
          </ol>
          <div className="flex gap-2 flex-wrap"><GoTo to="/app/coachs" label="Espace Coach" /><GoTo to="/app/couverture-curriculum" label="Couverture Curriculum" /></div>
        </Card>

        <Card icon={Award} id="formation" title="7. Ta progression dans ta discipline & tes formations" subtitle="Où tu es rendu, ce qu'il te reste">
          <ol className="space-y-2">
            <Step n="1">Ta fiche <b>Espace Coach</b> affiche ta ceinture et ta discipline principale — ton niveau de référence.</Step>
            <Step n="2">Utilise le <b>Carnet Progression</b> / la <b>Grille Kaizen V2</b> comme feuille de route de ta propre montée en grade.</Step>
            <Step n="3"><b>Certificats</b> archive tes accréditations et celles de l'équipe.</Step>
            <Step n="4">Point d'équipe suggéré : chaque coach dit « où il est rendu » et « sa prochaine formation/ceinture visée » — on le note dans sa fiche.</Step>
          </ol>
          <div className="flex gap-2 flex-wrap"><GoTo to="/app/coachs" label="Espace Coach" /><GoTo to="/app/certificats" label="Certificats" /></div>
        </Card>

        <Card icon={Rocket} id="equipe" title="8. Piloter l'évolution de ton équipe" subtitle="Objectifs par coach + suivi mensuel">
          <ol className="space-y-2">
            <Step n="1">Dans <b>Espace Coach</b>, fixe un <b>objectif clair à chaque coach</b> (déjà en place pour Sonia et Karl — ajoute-en pour les autres).</Step>
            <Step n="2">Revois ces objectifs à chaque rencontre d'équipe : atteint / en cours / à revoir.</Step>
            <Step n="3">Croise avec <b>Couverture Curriculum</b> (est-ce que tout le programme est enseigné ?) et les <b>Présences</b> (les groupes sont-ils remplis ?).</Step>
            <Step n="4">Utilise <b>Tâches & Notifications</b> pour assigner des actions de suivi entre les rencontres.</Step>
          </ol>
          <div className="flex gap-2 flex-wrap"><GoTo to="/app/coachs" label="Espace Coach" /><GoTo to="/app/taches" label="Tâches & Notifications" /></div>
        </Card>

        <Card icon={CheckCircle2} id="checklist" title="9. Recommandations à voir avec l'équipe ce soir" subtitle="Checklist de rencontre">
          <ul className="space-y-2">
            {[
              "Chaque coach connaît où prendre les présences et le fait à CHAQUE cours.",
              "Chaque élève a un carnet à jour (ceinture, techniques cochées, notes).",
              "Chaque coach a UN objectif pédagogique écrit dans sa fiche Espace Coach.",
              "Les plans de cours couvrent bien le curriculum de la ceinture visée.",
              "On recommande les Entraînements associés aux élèves entre les cours.",
              "On confirme : aucune ceinture ne monte sans évaluation validée par un coach.",
              "Chaque coach dit où il est rendu dans sa discipline et sa prochaine formation.",
              "On fixe la prochaine date de révision des objectifs d'équipe.",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </Card>

        <div className="border border-zinc-800 rounded-2xl p-5 bg-zinc-900/40 flex items-center gap-3">
          <BookOpen className="w-6 h-6 text-amber-400" />
          <p className="text-sm text-zinc-300">Astuce présentation : ouvre chaque module en direct pendant la rencontre grâce aux boutons « Ouvrir » ci-dessus. Tu peux aussi imprimer ce guide en PDF pour le remettre à l'équipe.</p>
        </div>
      </div>
    </div>
  );
}
